import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, map, catchError, of } from 'rxjs';
import { ClientRecordsService } from '../../core/services/client-records.service';
import { ContactResponseDto } from '@qc/shared';
import {
    ContactWithRole,
    ContactFormData,
    ContactFormConfig,
    ContactOperationResult,
} from '../interfaces/contact.interface';

@Injectable({
    providedIn: 'root',
})
export class ContactFormService {
    private clientRecordsService = inject(ClientRecordsService);
    private fb = inject(FormBuilder);

    private readonly defaultConfig: ContactFormConfig = {
        showRoleFields: true,
        showContextField: true,
        requiredFields: ['fullName', 'email'],
        maxLengths: {
            fullName: 255,
            positionTitle: 255,
            email: 255,
            phone: 50,
            context: 255,
            roleDescription: 255,
        },
    };

    createContactForm(config: Partial<ContactFormConfig> = {}): FormGroup {
        const finalConfig = { ...this.defaultConfig, ...config };

        const formControls: any = {
            id: [''],
            entityContactId: [''],
            fullName: ['', this.getFieldValidators('fullName', finalConfig)],
            positionTitle: ['', this.getFieldValidators('positionTitle', finalConfig)],
            email: ['', this.getFieldValidators('email', finalConfig)],
            phone: ['', this.getFieldValidators('phone', finalConfig)],
        };

        if (finalConfig.showContextField) {
            formControls.context = ['', this.getFieldValidators('context', finalConfig)];
        }

        if (finalConfig.showRoleFields) {
            formControls.roleDescription = ['', this.getFieldValidators('roleDescription', finalConfig)];
        }

        return this.fb.group(formControls);
    }

    private getFieldValidators(fieldName: string, config: ContactFormConfig): any[] {
        const validators: any[] = [];

        if (config.requiredFields.includes(fieldName)) {
            validators.push(Validators.required);
        }

        if (config.maxLengths[fieldName]) {
            validators.push(Validators.maxLength(config.maxLengths[fieldName]));
        }

        if (fieldName === 'email') {
            validators.push(Validators.email);
        }

        return validators;
    }

    createContact(contactData: ContactFormData): Observable<ContactOperationResult> {
        const { id, entityContactId, context, roleDescription, ...coreContactData } = contactData;

        return this.clientRecordsService.createContact(coreContactData).pipe(
            map((createdContact: ContactResponseDto) => ({
                success: true,
                contact: {
                    ...createdContact,
                    roleDescription: roleDescription || '',
                    context: context || '',
                },
            })),
            catchError((error) =>
                of({
                    success: false,
                    error: error.message || 'Failed to create contact',
                }),
            ),
        );
    }

    updateContact(contactId: string, contactData: ContactFormData): Observable<ContactOperationResult> {
        return this.clientRecordsService.updateContact(contactId, contactData).pipe(
            map((updatedContact: ContactResponseDto) => ({
                success: true,
                contact: {
                    ...updatedContact,
                    roleDescription: contactData.roleDescription || '',
                    context: contactData.context || '',
                },
            })),
            catchError((error) =>
                of({
                    success: false,
                    error: error.message || 'Failed to update contact',
                }),
            ),
        );
    }

    convertContactToFormData(contact: ContactWithRole): ContactFormData {
        return {
            id: contact.id,
            entityContactId: '',
            fullName: contact.fullName,
            positionTitle: contact.positionTitle,
            email: contact.email,
            phone: contact.phone,
            context: contact.context || '',
            roleDescription: contact.roleDescription || '',
        };
    }

    convertResponseToContactWithRole(
        response: ContactResponseDto,
        roleDescription = '',
        context = '',
    ): ContactWithRole {
        return {
            ...response,
            roleDescription,
            context,
        };
    }

    hasFieldError(form: FormGroup, fieldName: string): boolean {
        const field = form.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(form: FormGroup, fieldName: string): string | null {
        const field = form.get(fieldName);
        if (!field || !field.errors || !field.touched) {
            return null;
        }

        if (field.errors['required']) {
            return `${this.getFieldDisplayName(fieldName)} is required`;
        }
        if (field.errors['maxlength']) {
            return `${this.getFieldDisplayName(fieldName)} is too long`;
        }
        if (field.errors['email']) {
            return 'Please enter a valid email address';
        }

        return null;
    }

    private getFieldDisplayName(fieldName: string): string {
        const fieldNames: { [key: string]: string } = {
            fullName: 'Full Name',
            positionTitle: 'Position Title',
            email: 'Email',
            phone: 'Phone',
            context: 'Context',
            roleDescription: 'Role Description',
        };
        return fieldNames[fieldName] || fieldName;
    }
}
